import {useLocalStorage} from '@/composables/use-local-storage.js'
import {useDebugMode} from '@/composables/use-debug-mode.js'
import {useSegment} from '@/composables/use-segment.js'
import {computed, ref} from 'vue'

/**
 * Last message seen composable
 */
export function useLastSeenMessage(channelId, enrollmentId) {

  //Composables
  const {getFromLocalStorage, setInLocalStorage} = useLocalStorage()
  const {debugLog} = useDebugMode()
  const {track} = useSegment()

  //Key for local storage
  const key = computed(() => {
    if (!channelId && !enrollmentId) {
      throw new Error('No channel or enrollment ID provided')
    }
    return `last-seen-${channelId || enrollmentId}`
  })

  //Last seen message
  const lastSeenMessage = ref(getFromLocalStorage(key.value, null))
  const lastSeenMessageId = computed(() => lastSeenMessage.value?.id)
  const hasLastSeenMessage = computed(() =>
    lastSeenMessage.value &&
    typeof lastSeenMessage.value === 'object' &&
    lastSeenMessage.value.id &&
    typeof lastSeenMessage.value.sentAt === 'number'
  )

  //Track seen messages
  const trackMessageSeen = message => {

    //Ignore if this is a new message without ID
    if (!message.id) {
      return
    }

    //If we already have a last seen message for this channel or enrollment,
    //check if the new message is newer than the last seen message
    if (!hasLastSeenMessage.value || Number(message.sentAt) > lastSeenMessage.value.sentAt) {

      //Get data
      const {id, sentAt} = message
      const data = {id, sentAt: Number(sentAt)}

      //Store and debug
      setInLocalStorage(key.value, data)
      debugLog('Last seen message updated:', data)

      //Track with Segment
      track('Message Viewed', {
        channelId,
        enrollmentId,
        messageId: id,
      })

      //Update ref
      lastSeenMessage.value = data
    }
  }

  //Clear last seen message
  const clearLastSeenMessage = () => {
    setInLocalStorage(key.value, null)
    debugLog('Last seen message cleared')
    lastSeenMessage.value = null
  }

  //Expose interface
  return {
    lastSeenMessage,
    lastSeenMessageId,
    hasLastSeenMessage,
    trackMessageSeen,
    clearLastSeenMessage,
  }
}
